// The System log's change entries (`change-entry`, P13): what a save changed, old → new, as the
// browser writes it right after the save (frontend/src/features/routing/logged-write.ts). Apps
// cannot read `audit_log` (platform D165), which stays the full record; this is the readable copy.

import type { CrudEntity } from "@axny/platform-contracts";
import type { ChangeAction } from "../entities";
import {
  CUSTOMER_ASSIGNMENTS,
  DIVISION_ABBREVS,
  CUSTOMER_NAMES,
  LIST_VALUES,
  PICKUPS,
  PRE_ROUTES,
  ROUTING_LINES,
  ROUTING_RULES,
  SHIPMENTS,
  SHIPPING_ROWS,
} from "../entities";
import { formatShortDay } from "./days";
import { timeShown } from "./time";
import type { ChangeEntryRecord, ShippingRowRecord } from "./types";
import { WAREHOUSE_FIELDS } from "./types";

/** One field of a save: its name and its value before and after, as stored (days YYYY-MM-DD). */
export interface FieldChange {
  f: string;
  from: string;
  to: string;
}

/** What a writer says of one save; the person, the version and the record id are added at write time. */
export interface ChangeSpec {
  action: ChangeAction;
  /** The routing line it is about, when there is one. */
  lineId?: string;
  /** The line as it read: "TJ MAXX · 2 PTs · PO 20 058230". */
  lineText?: string;
  changes?: readonly FieldChange[];
  /** The save in plain words, when its fields do not say it ("Scheduled pickup 10/16 · FedEx …"). */
  note?: string;
}

/** `changes` is NVARCHAR(4000): a long value is cut, and fields past the limit are dropped. */
const MAX_CHANGES = 4000;
const MAX_VALUE = 400;

const asText = (v: unknown) => (v === null || v === undefined ? "" : String(v));

/** A field's value before and after, both as text; nothing when they are the same. */
export function fieldChange(f: string, from: unknown, to: unknown): FieldChange[] {
  const a = asText(from);
  const b = asText(to);
  return a === b ? [] : [{ f, from: a, to: b }];
}

/** Every field of `after` that differs from `before`. */
export function diffFields(
  before: Readonly<Record<string, unknown>>,
  after: Readonly<Record<string, unknown>>,
  fields: readonly string[],
): FieldChange[] {
  return fields.flatMap((f) => fieldChange(f, before[f], after[f]));
}

export function encodeChanges(changes: readonly FieldChange[]): string {
  const cut = (s: string) => (s.length > MAX_VALUE ? `${s.slice(0, MAX_VALUE - 1)}…` : s);
  const kept: FieldChange[] = [];
  for (const c of changes) {
    const next = [...kept, { f: c.f, from: cut(c.from), to: cut(c.to) }];
    if (JSON.stringify(next).length > MAX_CHANGES) break;
    kept.push(next.at(-1)!);
  }
  return kept.length ? JSON.stringify(kept) : "";
}

/**
 * One change as the System log shows it — read from `change-entry` today, from `audit_log` once
 * the platform lets an app read it (P13): the page reads only this.
 */
export interface ChangeView {
  id: string;
  /** When it was saved: ISO with the time, or the day alone (`timeKnown` false). */
  at: string;
  /** `at` has its time: the entry's `savedAt`. An older entry, or one whose clock disagrees with the store's day, has the day only. */
  timeKnown: boolean;
  personId: string;
  personName: string;
  entity: string;
  recordId: string;
  lineId: string;
  action: ChangeAction;
  changes: FieldChange[];
  note: string;
  lineText: string;
  /** The entry itself was changed after it was written (it is created only): not to be trusted. */
  edited: boolean;
  /** Its person is not who the store says saved it (`created_by`): not to be trusted. */
  otherWriter: boolean;
}

/** `GET /api/routing/changes`: the changes asked for, newest first. */
export interface ChangesPayload {
  changes: ChangeView[];
}

/** A stored time as ISO text: the server reads a Date, the browser its JSON. */
const isoOf = (v: unknown) => (v instanceof Date ? v.toISOString() : asText(v));

/**
 * The time is the entry's own `savedAt` — used only when its UTC day is within a day of the
 * store's `created_at`. Before D165 the list gave `created_at` as a day; it now has the time too.
 */
function savedTime(r: ChangeEntryRecord): { at: string; timeKnown: boolean } {
  const stored = isoOf(r.created_at).slice(0, 10);
  const saved = asText(r.savedAt);
  const d = new Date(saved);
  if (
    saved &&
    !Number.isNaN(d.getTime()) &&
    (!stored || Math.abs(Date.parse(`${stored}T12:00:00Z`) - d.getTime()) < 36 * 3600_000)
  )
    return { at: d.toISOString(), timeKnown: true };
  return { at: stored, timeKnown: false };
}

export function toChangeView(r: ChangeEntryRecord): ChangeView {
  const { at, timeKnown } = savedTime(r);
  return {
    id: r.id,
    at,
    timeKnown,
    personId: asText(r.personId),
    personName: asText(r.personName) || asText(r.personId),
    entity: asText(r.entity),
    recordId: asText(r.recordId),
    lineId: asText(r.lineId),
    action: r.action,
    changes: decodeChanges(r.changes),
    note: asText(r.note),
    lineText: asText(r.lineText),
    // The store's days only (the CRUD list has no time): an edit the same day is not seen here.
    edited: !!r.updated_at && !!r.created_at && isoOf(r.updated_at).slice(0, 10) !== isoOf(r.created_at).slice(0, 10),
    otherWriter: !!r.created_by && r.created_by !== r.personId,
  };
}

const ENTITIES: readonly CrudEntity[] = [
  ROUTING_LINES,
  PICKUPS,
  SHIPPING_ROWS,
  SHIPMENTS,
  LIST_VALUES,
  PRE_ROUTES,
  DIVISION_ABBREVS,
  CUSTOMER_NAMES,
  ROUTING_RULES,
  CUSTOMER_ASSIGNMENTS,
];

/** A field's label as the grid says it: READY DATE, Routing Notes, Carrier. */
export function fieldLabel(entity: string, field: string): string {
  if (field === "reviewStatus") return "Review";
  if (field === "finishedStatus") return "Finished";
  if (field === "status") return entity === "pickup" ? "Pickup" : "Status";
  // The schedule's header is "PICK UP # / SHIPMENT # / BOL #"; in a sentence, its first words.
  if (field === "loadRef") return "PICK UP #";
  return ENTITIES.find((e) => e.id === entity)?.fields.find((f) => f.name === field)?.label ?? field;
}

/** A stored value as people read it: a day as 10/14, nothing as "blank". */
const shown = (v: string) => (/^\d{4}-\d{2}-\d{2}/.test(v) ? formatShortDay(v.slice(0, 10)) : v);
/** A time (In:, Out:, App Time) as every page shows it, and a moment (PE Order Start / End Time) its day and time. */
const TIME_FIELDS: readonly string[] = ["inTime", "outTime", "appTime"];
const MOMENT_FIELDS: readonly string[] = ["peStartAt", "peEndAt"];
const valueShown = (f: string, v: string) =>
  TIME_FIELDS.includes(f)
    ? timeShown(v)
    : MOMENT_FIELDS.includes(f) && /^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}$/.test(v)
      ? `${formatShortDay(v.slice(0, 10))} ${timeShown(v.slice(11).replace(/^0(?=\d)/, ""))}`
      : shown(v);

/** A tick (a routing rule's No routing rule, Separate from group: "1" / "0") as Yes / No. */
const TICKS: Readonly<Record<string, readonly string[]>> = { "routing-rule": ["noRule", "separate"] };
const tick = (entity: string, f: string, v: string) =>
  TICKS[entity]?.includes(f) ? (v === "1" ? "Yes" : v === "0" ? "No" : v) : v;

/** One field in plain words: "READY DATE 10/14 (was 10/17)", "Routing Notes: Requested in TMS 10/12". */
export function fieldText(entity: string, change: FieldChange): string {
  const c = { ...change, from: tick(entity, change.f, change.from), to: tick(entity, change.f, change.to) };
  const label = fieldLabel(entity, c.f);
  if (!c.to) return `${label} cleared (was ${valueShown(c.f, c.from)})`;
  // A date, and a pickup's short values (Carrier FedEx, PICK UP # RTS# 2222222), read without a colon.
  const bare =
    /^\d{4}-\d{2}-\d{2}/.test(c.to) || /^\d{4}-\d{2}-\d{2}/.test(c.from) || (entity === "pickup" && c.f !== "notes");
  // "In:" and "Out:" already end in a colon (the sheet's headers).
  const now = bare ? `${label} ${valueShown(c.f, c.to)}` : `${label.replace(/:$/, "")}: ${valueShown(c.f, c.to)}`;
  return c.from ? `${now} (was ${valueShown(c.f, c.from)})` : now;
}

/**
 * A change in plain words, with the grid's labels; several fields saved at once are one text. The
 * actions a note says best (schedule, ship, a list…) are their note.
 */
export function changeText(c: Pick<ChangeView, "action" | "entity" | "changes" | "note">): string {
  const fields = c.changes.map((x) => fieldText(c.entity, x)).join(" · ");
  // A pickup's field change names the pickup, as its note does: "Pickup 10/26: …  · 2 lines".
  const onPickup = (text: string) => {
    const parts = c.note.split(" · ");
    const count = parts.length > 1 && / lines?$/.test(parts.at(-1)!) ? ` · ${parts.at(-1)}` : "";
    return `${parts[0] || "Pickup"}: ${text}${count}`;
  };
  switch (c.action) {
    case "approve":
      return `Approved${c.changes[0]?.from ? ` (was ${c.changes[0].from})` : ""}`;
    case "restore": {
      const text = `Restored ${c.changes.map((x) => `${fieldLabel(c.entity, x.f)} to ${valueShown(x.f, tick(c.entity, x.f, x.to)) || "blank"} (was ${valueShown(x.f, tick(c.entity, x.f, x.from)) || "blank"})`).join(" · ")}`;
      return c.entity === "pickup" ? onPickup(text) : text;
    }
    case "choose":
      return `Chose ${fields} (2 values)`;
    case "edit":
      return [fields, c.note].filter(Boolean).join(" — ") || "Saved";
    case "pickup": {
      // A new pickup, or one shipped / cancelled with its last line, says so in its note.
      if (!c.changes.length || c.changes.some((x) => x.f === "status")) return c.note || fields;
      // "Pickup 10/26: Carrier FedEx (was UPS) · 2 lines": the note's first part, the fields, its line count.
      return onPickup(fields);
    }
    case "pre-route":
      // A pre-route added, deleted, moved or closed by A2000: its note says it; a line's move, its fields too.
      return [fields, c.note].filter(Boolean).join(" — ") || "Pre-route";
    default:
      return c.note || fields || c.action;
  }
}

/** When a line was on a pickup: ISO times, or a day alone; "" is open (before the log, still on it). */
export interface PickupSpan {
  pickupId: string;
  from: string;
  to: string;
}

const byId = (a: { id: string }, b: { id: string }) => a.id.localeCompare(b.id, undefined, { numeric: true });
const byTime = (a: ChangeView, b: ChangeView) => a.at.localeCompare(b.at) || byId(a, b);

/** `a` before `b`; a day alone is compared as its day, so the same day is never before. */
const before = (a: string, b: string) => (a.length === 10 || b.length === 10 ? a.slice(0, 10) < b.slice(0, 10) : a < b);

/**
 * When a line was on each pickup it has a shipping row on, from the line's own entries: it joined
 * at a "Scheduled pickup" (schedule, add-line) and left at its next "Taken off" (remove); a row
 * Shipped keeps its pickup's span open (the line went out on that truck). Rows and joins pair in
 * the order they were made — the last row with the last join — so a row older than the log has
 * no start. An entry given twice counts once.
 */
export function pickupSpans(
  lineChanges: readonly ChangeView[],
  rows: readonly Pick<ShippingRowRecord, "id" | "pickupId" | "status">[],
): PickupSpan[] {
  const own = [...new Map(lineChanges.map((c) => [c.id, c])).values()].sort(byTime);
  const joins = own.filter(
    (c) => (c.action === "schedule" || c.action === "add-line") && c.note.startsWith("Scheduled pickup"),
  );
  const sorted = [...rows].sort(byId);
  return sorted.map((r, i) => {
    const join = joins[joins.length - sorted.length + i];
    const from = join?.at ?? "";
    const left =
      r.status === "Removed" ? own.find((c) => c.action === "remove" && (!from || !before(c.at, from))) : undefined;
    return { pickupId: r.pickupId, from, to: left?.at ?? "" };
  });
}

/** A pickup's entry falls while the line was on it. */
export const whileOnPickup = (c: Pick<ChangeView, "recordId" | "at">, spans: readonly PickupSpan[]) =>
  spans.some((s) => s.pickupId === c.recordId && (!s.from || !before(c.at, s.from)) && (!s.to || !before(s.to, c.at)));

/** The fields of an entry; [] when it holds none or is unreadable. */
export function decodeChanges(text: string | null | undefined): FieldChange[] {
  if (!text) return [];
  try {
    const v: unknown = JSON.parse(text);
    return Array.isArray(v)
      ? v
          .filter((c): c is Record<string, unknown> => typeof c === "object" && c !== null && typeof c.f === "string")
          .map((c) => ({ f: String(c.f), from: asText(c.from), to: asText(c.to) }))
      : [];
  } catch {
    return [];
  }
}

/** The fields Restore may put back, per entity (spec §3.15): what people type, never a status. */
export const RESTORABLE: Readonly<Record<string, readonly string[]>> = {
  "routing-line": WAREHOUSE_FIELDS,
  // Not PLTS since 017: the line's PE Order Pallets holds it (restored as a line field).
  "shipping-row": ["ctns", "loader", "inTime", "outTime", "trailer", "wmsAsnTrip", "notes"],
  // Not the carrier: it is picked from A2000's or Deposco's list (item 8), never put back as text.
  pickup: ["pickupDate", "appTime", "door", "loadRef", "notes"],
  "pre-route": ["routingRef", "appointmentDate", "notes"],
  // "" is the division's default abbreviation: its first save restores to it.
  "division-abbrev": ["abbreviation"],
  // "" is the source's own rule (Admin → Customers).
  "customer-name": ["shownName"],
  // Admin → Routing rules: the days, No routing rule and Separate from group ("1" / "0").
  "routing-rule": ["openBdays", "dueBdays", "noRule", "separate", "notes"],
};
/** The actions that are field changes; a schedule, a shipment or an approval is undone on its page. */
const FIELD_ACTIONS: readonly ChangeAction[] = ["edit", "choose", "restore", "pickup"];
/** What the Restore column calls a change that is not a field's. */
const NOT_A_FIELD: Partial<Record<ChangeAction, string>> = {
  approve: "An approval",
  schedule: "A schedule",
  "add-line": "A line added",
  remove: "A line taken off",
  ship: "A shipment",
  finish: "A line finished",
  list: "A list change",
  "pre-route": "A pre-route",
  review: "A review",
};

/** One field Restore puts back: its value before the change, what the change made it, and now. */
export interface RestoreField {
  f: string;
  label: string;
  /** The value Restore saves: the one before the change. */
  restoreTo: string;
  /** What the change made it. */
  changedTo: string;
  /** What it is now. */
  now: string;
  /** Changed again since: now is not what the change made it. */
  changedSince: boolean;
}

/** Not restorable: `short` is what the Restore column says, `why` the whole reason (on hover). */
export type RestorePlan =
  { ok: true; fields: RestoreField[]; changedSince: boolean } | { ok: false; short: string; why: string };

const no = (short: string, why: string): RestorePlan => ({ ok: false, short, why });

/**
 * What Restore of a change would do (spec §3.15): only a field change of a line, a shipping row,
 * a pickup, a waiting pre-route or a division's abbreviation — not a create, a delete, a schedule or a shipment, not a line from before WMS
 * grouping, and not an entry that was changed after it was written. `current` is the record's
 * values now (null: no longer there); a field changed again since is said, with its value now.
 */
export function restorePlan(
  c: Pick<ChangeView, "entity" | "action" | "changes" | "edited" | "otherWriter">,
  current: Readonly<Record<string, unknown>> | null,
  opts: {
    finished?: boolean;
    legacy?: boolean;
    /**
     * Why what the change is on is closed now — its line shipped or cancelled, its pickup shipped
     * or cancelled, its row taken off: "Line shipped — can't restore" (the column says what is before
     * " — "). One rule for every entity.
     */
    closed?: string | null;
  } = {},
): RestorePlan {
  const fields = RESTORABLE[c.entity];
  if (!fields)
    return no(
      "Not this kind of record",
      "Only changes to a line, a shipping row, a pickup, a pre-route or a division abbreviation can be restored",
    );
  if (c.edited || c.otherWriter) return no("Entry edited later", "This entry was changed after it was written");
  if (!FIELD_ACTIONS.includes(c.action))
    return no(`${NOT_A_FIELD[c.action] ?? "Not a field change"}: not restored`, "Only a field change can be restored");
  // A pickup's first entry is its creation: every field from blank, the status with them.
  if (c.changes.some((x) => x.f === "status" && !x.from)) return no("Record created", "A new record can't be restored");
  if (opts.legacy) return no("Before WMS grouping", "A line from before WMS grouping is never written again");
  // "Line shipped — can't restore": the column says "Line shipped".
  if (opts.closed) return no(opts.closed.split(" — ")[0]!, opts.closed);
  if (!current) return no("Record gone", "That record is no longer there");
  if (opts.finished) return no("Line finished", "The line is finished: History is read-only");
  const out = c.changes
    .filter((x) => fields.includes(x.f))
    .map((x) => {
      // A stored day may come back as a time (2026-10-16T00:00:00.000Z): compared as the day.
      const v = asText(current[x.f]);
      const now = /^\d{4}-\d{2}-\d{2}T/.test(v) ? v.slice(0, 10) : v;
      return {
        f: x.f,
        label: fieldLabel(c.entity, x.f),
        restoreTo: x.from,
        changedTo: x.to,
        now,
        changedSince: now !== x.to,
      };
    })
    .filter((x) => x.now !== x.restoreTo);
  if (!out.length)
    return no("Already the old value", "Nothing to restore: it already has the old value (a later change put it back)");
  return { ok: true, fields: out, changedSince: out.some((x) => x.changedSince) };
}

/** "READY DATE 10/14 → 10/17": one field of a Restore, as its dialog says it. */
export const restoreText = (x: RestoreField) =>
  `${x.label}: ${valueShown(x.f, x.changedTo) || "blank"} → ${valueShown(x.f, x.restoreTo) || "blank"}${x.changedSince ? ` (now ${valueShown(x.f, x.now) || "blank"})` : ""}`;
